import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm, parseHttpError } from '@upme/api-client';
import type { CityDto, EventCategoryDto, PublishFromImportInput, SourceMessageDto } from '@upme/contracts';
import { PublishFromImportInput as PFISchema } from '@upme/contracts';
import { bcp47, resolveLocale } from '@upme/i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { useEffect, useMemo, useState } from 'react';
import { type Resolver, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { z } from 'zod';
import { collectRootMessages } from '../../../app/form-errors';
import { zodResolver } from '../../../app/zod-resolver';
import { CategoryMultiSelect } from '../../events/components/category-multi-select';
import {
  type DuplicateConflictError,
  isDuplicateConflict,
  usePublishFromImport,
  useResolveImportPlace,
} from '../mutations';
import { PlaceMatchCombobox } from './place-match-combobox';

export type PublishFromImportFormValues = {
  cityId: string;
  title: string;
  description?: string | undefined;
  venueText: string;
  startsAtLocalDates: string[];
  startsAtLocalTime: string | null;
  endsAtLocalDate?: string | undefined;
  endsAtLocalTime?: string | undefined;
  price?: number | null | undefined;
  organizerUsername?: string | undefined;
  coverImageUrl?: string | undefined;
  videoUrl?: string | undefined;
  placeId?: string | null;
  categoryIds: string[];
  publish: boolean;
};

/** Результат submitEvent: опубликовано / провал валидации / найден дубль (форма показывает диалог force-override). */
export type SubmitEventResult = 'published' | 'invalid' | 'duplicate';

export interface PublishFromImportFormApi {
  submitEvent: (eventCategories: ReadonlyArray<EventCategoryDto>) => Promise<SubmitEventResult>;
  getPayload: (eventCategories: ReadonlyArray<EventCategoryDto>) => PublishFromImportInput | null;
  getFeedFields: () => { title?: string; body?: string };
  form: UseFormReturn<PublishFromImportFormValues>;
}

interface Props {
  item: SourceMessageDto;
  cities: ReadonlyArray<CityDto>;
  categories: ReadonlyArray<EventCategoryDto>;
  onPublishReady?: (api: PublishFromImportFormApi) => void;
}

function buildPayloadFromValues(
  values: PublishFromImportFormValues,
  categoryIds: string[],
): PublishFromImportInput {
  const cleanedDates = values.startsAtLocalDates.filter((d) => d);
  const multi = cleanedDates.length > 1;
  return {
    ...values,
    categoryIds,
    description: values.description || undefined,
    organizerUsername: values.organizerUsername || undefined,
    coverImageUrl: values.coverImageUrl || undefined,
    videoUrl: values.videoUrl || undefined,
    startsAtLocalDates: cleanedDates,
    startsAtLocalTime: values.startsAtLocalTime ? values.startsAtLocalTime : null,
    endsAtLocalDate: multi ? undefined : values.endsAtLocalDate || undefined,
    endsAtLocalTime: multi ? undefined : values.endsAtLocalTime || undefined,
    price: values.price === undefined || Number.isNaN(values.price) ? null : values.price,
    placeId: values.placeId ?? null,
    force: false,
  };
}

function resolveEventCategoryIds(
  values: PublishFromImportFormValues,
  eventCategories: ReadonlyArray<EventCategoryDto>,
): string[] {
  if (values.categoryIds.length > 0) return values.categoryIds;
  const other = eventCategories.find((c) => c.slug === 'other');
  return other ? [other.id] : values.categoryIds;
}

export function PublishFromImportForm({ item, cities, categories, onPublishReady }: Props) {
  const { t, i18n } = useTranslation('import-queue');
  const defaults = useMemo<PublishFromImportFormValues>(() => {
    const fields = item.extracted?.fields;
    const dates = fields?.startsAtLocalDates ?? [];
    const initialDates = dates.length > 0 ? dates : [''];
    const hasEnds = Boolean(fields?.endsAtLocalDate && fields?.endsAtLocalTime) && initialDates.length <= 1;
    const mappedCategoryIds = (fields?.categorySlugs ?? [])
      .map((slug) => categories.find((c) => c.slug === slug)?.id)
      .filter((id): id is string => Boolean(id));
    return {
      cityId: item.channel.cityId,
      title: fields?.title ?? '',
      description: fields?.description ?? undefined,
      venueText: fields?.venueText ?? '',
      startsAtLocalDates: initialDates,
      startsAtLocalTime: fields?.startsAtLocalTime ?? '',
      endsAtLocalDate: hasEnds ? (fields?.endsAtLocalDate ?? undefined) : undefined,
      endsAtLocalTime: hasEnds ? (fields?.endsAtLocalTime ?? undefined) : undefined,
      price: fields?.price ?? null,
      organizerUsername: fields?.organizerUsername ?? undefined,
      coverImageUrl: item.mediaUrls[0] ?? undefined,
      videoUrl: item.videoPreviewUrl ?? undefined,
      placeId: null,
      categoryIds: mappedCategoryIds,
      publish: true,
    };
  }, [item, categories]);

  const form = useForm<PublishFromImportFormValues>({
    resolver: zodResolver(
      PFISchema as unknown as z.ZodType<PublishFromImportFormValues>,
    ) as Resolver<PublishFromImportFormValues>,
    defaultValues: defaults,
    mode: 'onSubmit',
  });

  const publish = usePublishFromImport(item.id);
  const publishEvent = publish.mutateAsync;
  const router = useRouter();

  // Кнопка «создать место из карты»: доступна, если экстрактор нашёл карт-ссылку.
  const venueMapUrl = item.extracted?.fields?.venueMapUrl ?? null;
  const resolvePlace = useResolveImportPlace(item.id);
  const handleResolvePlace = async () => {
    try {
      const { placeId, venueText } = await resolvePlace.mutateAsync();
      form.setValue('placeId', placeId, { shouldValidate: false });
      if (venueText) form.setValue('venueText', venueText, { shouldValidate: false });
      toast.success(t('publishForm.placeLinkedToast', { venueText }));
    } catch {
      toast.error(t('publishForm.placeResolveError'));
    }
  };

  const [duplicateConflict, setDuplicateConflict] = useState<DuplicateConflictError | null>(null);
  const [pendingPayload, setPendingPayload] = useState<PublishFromImportInput | null>(null);

  useEffect(() => {
    if (!onPublishReady) return;
    const api: PublishFromImportFormApi = {
      form,
      getPayload: (eventCategories) => {
        const values = form.getValues();
        const categoryIds = resolveEventCategoryIds(values, eventCategories);
        const payload = buildPayloadFromValues(values, categoryIds);
        const parsed = PFISchema.safeParse(payload);
        return parsed.success ? parsed.data : null;
      },
      getFeedFields: () => {
        const title = form.getValues('title')?.trim();
        const description = form.getValues('description')?.trim();
        const out: { title?: string; body?: string } = {};
        if (title) out.title = title;
        if (description) out.body = description;
        return out;
      },
      submitEvent: async (eventCategories) => {
        let result: SubmitEventResult = 'invalid';
        await form.handleSubmit(
          async (values) => {
            const categoryIds = resolveEventCategoryIds(values, eventCategories);
            const payload = buildPayloadFromValues(values, categoryIds);
            try {
              await publishEvent(payload);
              result = 'published';
            } catch (err) {
              if (isDuplicateConflict(err)) {
                // Дубль: показываем диалог force-override; caller не продвигает очередь.
                setDuplicateConflict(err.conflict);
                setPendingPayload({ ...payload, force: true });
                result = 'duplicate';
              } else if ((await parseHttpError(err))?.message === 'organizer_blocked') {
                // #190: понятная причина вместо кода; снять блокировку — на странице блокировок.
                form.setError('root.server', { type: 'server', message: t('publishForm.organizerBlocked') });
                result = 'invalid';
              } else {
                await mapApiErrorToForm(err, form);
                result = 'invalid';
              }
            }
          },
          () => {
            result = 'invalid';
          },
        )();
        return result;
      },
    };
    onPublishReady(api);
  }, [form, onPublishReady, publishEvent, t]);

  const cityId = form.watch('cityId');
  const city = cities.find((c) => c.id === cityId);
  const tzLabel = city ? city.timezone : '';
  const categoryIds = form.watch('categoryIds') ?? [];

  const dates = form.watch('startsAtLocalDates') ?? [];
  const isMultiDate = dates.length > 1;

  const addDate = () => {
    form.setValue('startsAtLocalDates', [...dates, ''], { shouldValidate: false });
  };
  const removeDate = (i: number) => {
    if (dates.length <= 1) return;
    form.setValue(
      'startsAtLocalDates',
      dates.filter((_, idx) => idx !== i),
      { shouldValidate: true },
    );
  };
  const updateDate = (i: number, v: string) => {
    const next = [...dates];
    next[i] = v;
    form.setValue('startsAtLocalDates', next, { shouldValidate: true });
  };

  // Force-override публикация дубля (после подтверждения в диалоге). Публикация
  // событий идёт через ImportQueueModerationActions → submitEvent; здесь — только
  // повторная отправка с force:true и переход на созданное событие.
  const doPublish = async (payload: PublishFromImportInput) => {
    const result = await publish.mutateAsync(payload);
    const created = result.events;
    toast.success(t('publishForm.eventCreated', { count: created.length }));
    const first = created[0];
    if (first) {
      router.navigate({ to: '/events/$eventId', params: { eventId: first.id } });
    }
  };

  const handleForceConfirm = async () => {
    if (!pendingPayload) return;
    try {
      await doPublish(pendingPayload);
    } catch (err) {
      await mapApiErrorToForm(err, form);
    } finally {
      setDuplicateConflict(null);
      setPendingPayload(null);
    }
  };

  const handleForceCancel = () => {
    setDuplicateConflict(null);
    setPendingPayload(null);
  };

  const err = (k: keyof PublishFromImportFormValues) => {
    const e = form.formState.errors[k] as { message?: string } | undefined;
    return e?.message ? <p className="text-sm text-destructive mt-1">{String(e.message)}</p> : null;
  };
  const rootMessages = collectRootMessages(form.formState.errors as Record<string, unknown>);

  return (
    <form className="space-y-5" noValidate onSubmit={(e) => e.preventDefault()}>
      <div>
        <Label>{t('publishForm.cityLabel')}</Label>
        <Select value={cityId} onValueChange={(v) => form.setValue('cityId', v, { shouldValidate: true })}>
          <SelectTrigger>
            <SelectValue placeholder={t('publishForm.selectCity')} />
          </SelectTrigger>
          <SelectContent>
            {cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {tzLabel && (
          <p className="text-xs text-muted-foreground mt-1">
            {t('publishForm.tzHintPrefix')}
            <code>{tzLabel}</code>
          </p>
        )}
        {err('cityId')}
      </div>

      <div>
        <Label htmlFor="title">{t('publishForm.titleLabel')}</Label>
        <Input id="title" {...form.register('title')} placeholder={t('publishForm.titlePlaceholder')} />
        {err('title')}
      </div>

      <div>
        <Label htmlFor="venueText">{t('publishForm.venueLabel')}</Label>
        <Input
          id="venueText"
          {...form.register('venueText')}
          placeholder={t('publishForm.venuePlaceholder')}
        />
        {err('venueText')}
      </div>

      <div>
        <Label>{t('publishForm.linkPlaceLabel')}</Label>
        <PlaceMatchCombobox
          cityId={cityId}
          value={form.watch('placeId') ?? null}
          onChange={(id) => form.setValue('placeId', id, { shouldValidate: false })}
          initialQuery={form.watch('venueText') ?? ''}
        />
        {venueMapUrl && (
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResolvePlace}
              disabled={resolvePlace.isPending}
            >
              {resolvePlace.isPending ? t('publishForm.resolvingEllipsis') : t('publishForm.resolveFromMap')}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              {t('publishForm.resolveMapHintPrefix')}
              <a href={venueMapUrl} target="_blank" rel="noreferrer" className="underline">
                {t('publishForm.resolveMapHintLink')}
              </a>
              {t('publishForm.resolveMapHintSuffix')}
            </p>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-1">{t('publishForm.placeEmptyHint')}</p>
      </div>

      <div>
        <Label htmlFor="description">{t('publishForm.descriptionLabel')}</Label>
        <textarea
          id="description"
          rows={4}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('description')}
        />
        {err('description')}
      </div>

      <div className="space-y-3">
        <div className="rounded-md border p-3 space-y-2">
          <Label>{t('publishForm.datesLabel')}</Label>
          <div className="flex flex-col gap-2">
            {dates.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="date"
                  value={d}
                  onChange={(e) => updateDate(i, e.target.value)}
                  className="max-w-[200px]"
                />
                {dates.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeDate(i)}
                    aria-label={t('publishForm.removeDate')}
                  >
                    ✕
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addDate} disabled={dates.length >= 20}>
            {t('publishForm.addDate')}
          </Button>
          {err('startsAtLocalDates')}
        </div>

        <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
          <div>
            <Label htmlFor="startsAtLocalTime">{t('publishForm.startTimeLabel')}</Label>
            <Input id="startsAtLocalTime" type="time" {...form.register('startsAtLocalTime')} />
            {err('startsAtLocalTime')}
          </div>
          {!isMultiDate && (
            <>
              <div>
                <Label htmlFor="endsAtLocalDate">{t('publishForm.endDateLabel')}</Label>
                <Input id="endsAtLocalDate" type="date" {...form.register('endsAtLocalDate')} />
                {err('endsAtLocalDate')}
              </div>
              <div>
                <Label htmlFor="endsAtLocalTime">{t('publishForm.endTimeLabel')}</Label>
                <Input id="endsAtLocalTime" type="time" {...form.register('endsAtLocalTime')} />
                {err('endsAtLocalTime')}
              </div>
            </>
          )}
        </div>
      </div>

      <div>
        <Label>{t('publishForm.categoriesLabel')}</Label>
        <CategoryMultiSelect
          categories={categories}
          value={categoryIds}
          onChange={(next) => form.setValue('categoryIds', next, { shouldValidate: true })}
        />
        {err('categoryIds')}
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        <div>
          <Label htmlFor="price">{t('publishForm.priceLabel')}</Label>
          <Input
            id="price"
            type="number"
            min={0}
            step={1}
            {...form.register('price', { valueAsNumber: true })}
          />
          {err('price')}
        </div>
        <div>
          <Label htmlFor="organizerUsername">{t('publishForm.organizerUsernameLabel')}</Label>
          <Input
            id="organizerUsername"
            {...form.register('organizerUsername')}
            placeholder={t('publishForm.organizerUsernamePlaceholder')}
          />
          {err('organizerUsername')}
        </div>
        <div>
          <Label htmlFor="coverImageUrl">{t('publishForm.coverImageUrlLabel')}</Label>
          <Input id="coverImageUrl" {...form.register('coverImageUrl')} placeholder="https://…" />
          {err('coverImageUrl')}
        </div>
        <div>
          <Label htmlFor="videoUrl">{t('publishForm.videoUrlLabel')}</Label>
          <Input id="videoUrl" {...form.register('videoUrl')} placeholder="https://…" />
          {err('videoUrl')}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input id="publish" type="checkbox" className="h-4 w-4" {...form.register('publish')} />
        <Label htmlFor="publish">{t('publishForm.publishNowLabel')}</Label>
      </div>

      {rootMessages.map((m) => (
        <p key={m} className="text-sm text-destructive">
          {m}
        </p>
      ))}

      {duplicateConflict && (
        <AlertDialog open onOpenChange={(open) => !open && handleForceCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('publishForm.duplicateTitle')}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p className="mb-2">
                    {t('publishForm.duplicateSimilarToPrefix')}
                    {duplicateConflict.code === 'duplicate_news'
                      ? t('publishForm.duplicateKindEventNews')
                      : t('publishForm.duplicateKindEvent')}
                    :
                  </p>
                  <ul className="list-disc pl-4 space-y-1 text-sm">
                    {duplicateConflict.candidates.map((c) => (
                      <li key={c.id}>
                        <span className="font-medium">{c.title}</span>
                        {c.startsAt && (
                          <span className="text-muted-foreground ml-1">
                            ({new Date(c.startsAt).toLocaleDateString(bcp47(resolveLocale(i18n.language)))})
                          </span>
                        )}
                        <span className="text-muted-foreground ml-1">
                          {t('publishForm.similaritySuffix', { pct: Math.round(c.similarity * 100) })}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2">{t('publishForm.confirmAnyway')}</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleForceCancel}>{t('publishForm.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleForceConfirm} disabled={publish.isPending}>
                {publish.isPending ? t('publishForm.publishingEllipsis') : t('publishForm.publish')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </form>
  );
}
